import {Fragment} from "react";
import {useTranslation} from "react-i18next";
import {css} from "@emotion/react";
import {Button} from "@pg-design/button";
import {flex, flexDirection, ph, pv, w100} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";

import {formatDate, shortDate} from "../../../../../utils/dates";
import {breakpoints} from "../../../common/app/styles/breakpoints";
import {truncateMultiline} from "../../../components/css_helpers/truncate";
import {IEntry} from "../../newshub/types/Entry";
import {LANDING_MARKET_NEWS_COLORS} from "./landingMarketNews.constants";
import {getArticleMetaItems, getArticlePath, isArticleNew} from "./landingMarketNewsArticle";

const {accentYellow, badgeOrange, panelDark} = LANDING_MARKET_NEWS_COLORS;

interface ILandingMarketNewsCtaProps {
    article: IEntry;
    language: string;
}

const scrollToNewsletter = () => {
    document.getElementById("newsletter")?.scrollIntoView({behavior: "smooth"});
};

export const LandingMarketNewsCta = ({article, language}: ILandingMarketNewsCtaProps) => {
    const {t} = useTranslation();
    const articlePath = getArticlePath(article, language);
    const metaItems = getArticleMetaItems(article).map((item, index) =>
        index === 0 ? formatDate(item, shortDate) : item
    );
    const badgeLabel = isArticleNew(article.publication_date)
        ? t("landing.market_news.badge_new")
        : t("landing.market_news.badge_recommended");

    return (
        <div css={content}>
            <span css={badge}>{badgeLabel}</span>

            <div css={metaRow}>
                {metaItems.map((item, index) => (
                    <Fragment key={`${item}-${index}`}>
                        {index > 0 && (
                            <Text css={metaSeparator} variant="body_copy_0" as="span" aria-hidden>
                                ·
                            </Text>
                        )}
                        <Text css={metaItem} variant="body_copy_0" as="span">
                            {item}
                        </Text>
                    </Fragment>
                ))}
            </div>

            <Text css={articleTitle} variant="headline_2">
                {article.title}
            </Text>

            {article.lead ? (
                <Text css={articleLead} variant="body_copy_1">
                    {article.lead}
                </Text>
            ) : null}

            <div css={actions}>
                <Button href={articlePath} css={[actionButton, readMoreButton]} size="medium" variant="banner_primary">
                    {t("landing.market_news.read_more")}
                </Button>
            </div>
        </div>
    );
};

const content = css`
    ${flex()};
    ${flexDirection("column")};
    gap: 16px;
    min-width: 0;
    ${w100};
    ${pv(4)};
    color: #fff;
    background: transparent;

    @media (min-width: ${breakpoints.sm}) {
        padding-inline: 0;
    }
`;

const badge = css`
    display: inline-flex;
    align-self: flex-start;
    padding: 4px 12px;
    border-radius: 999px;
    background: ${badgeOrange};
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
`;

const metaRow = css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;

const metaItem = css`
    color: #fff;
`;

const metaSeparator = css`
    color: rgba(255, 255, 255, 0.6);
`;

const articleTitle = css`
    margin: 0;
    color: #fff;
`;

const articleLead = css`
    margin: 0;
    color: rgba(255, 255, 255, 0.85);
    ${truncateMultiline(3)};
    flex-shrink: 0;
    max-width: 640px;
`;

const actions = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
    ${w100};

    @media (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        align-items: stretch;
    }
`;

const actionButton = css`
    && {
        ${w100};
        text-transform: uppercase;

        @media (min-width: ${breakpoints.sm}) {
            flex: 1 1 0;
            min-width: 0;
            max-width: 265px;
        }
    }
`;

const readMoreButton = css`
    && {
        background-color: ${accentYellow};
        border-color: ${accentYellow};
        color: ${panelDark};
        fill: ${panelDark};

        &:hover {
            color: ${panelDark};
        }
    }
`;

const newsletterButton = css`
    && {
        border-color: #fff;
        background: transparent;
        color: #fff;

        &:hover {
            border-color: #fff;
            background: rgba(255, 255, 255, 0.08);
            color: #fff;
        }
    }
`;
